import React, { useState } from 'react';
import { useJWTAuth } from '../auth/JWTAuthContext';
import { useToast } from '../contexts/ToastContext';

const OperatorChatbox = ({ roomId, gameWidth }) => {
  const [operatorCommand, setOperatorCommand] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = React.useRef(null);
  const { token } = useJWTAuth();
  const { error } = useToast();

  // Handle local operator command submission
  const handleLocalCommand = async (e) => {
    e.preventDefault();
    if (!operatorCommand.trim() || isSubmitting) return;

    if (!token) {
      error('Please log in to send commands');
      return;
    }

    setIsSubmitting(true);
    try {
      // Get session ID from BattleStateManager or window, fallback to roomId
      let targetRoomId = roomId;
      if (!targetRoomId) {
        try {
          const BattleStateManager = (await import('../game/BattleStateManager.js')).default;
          targetRoomId = BattleStateManager.getSessionId();
        } catch (_) {}
      }
      if (!targetRoomId && typeof window !== 'undefined') {
        targetRoomId = window.__SW_SESSION_ID__ || window.__SW_SESSION_ROOM_ID__;
      }
      if (!targetRoomId) {
        throw new Error('No active game session. Please launch the game first.');
      }
      const response = await fetch('/api/chat/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        },
        body: JSON.stringify({
          message: operatorCommand.trim(),
          roomId: targetRoomId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to send command' }));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      // Success - clear input
      setOperatorCommand('');

      // After line 56 where setOperatorCommand('') is called, or in the finally block:
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } catch (err) {
      console.error('[OperatorChatbox] Failed to send local command:', err);
      error(err.message || 'Failed to send command');
    } finally {
      setIsSubmitting(false);

      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  return (
    <div style={{
      width: gameWidth || '100%',
      maxWidth: gameWidth || '100%',
      marginTop: '0.2rem',
      transition: 'width 0.3s ease, max-width 0.3s ease'
    }}>
      <form onSubmit={handleLocalCommand} style={{
        display: 'flex',
        gap: '0.5rem',
        width: '100%'
      }}>
        <input
          ref={inputRef}
          type="text"
          value={operatorCommand}
          onChange={(e) => setOperatorCommand(e.target.value)}
          placeholder="Enter command (e.g., join, buy 1, heal, balance)"
          disabled={isSubmitting || !token}
          style={{
            flex: 1,
            padding: '0.2rem 0.5rem',
            backgroundColor: '#111827',
            border: '1px solid #374151',
            borderRadius: '0.375rem',
            color: '#F3F4F6',
            fontSize: '0.75rem',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            transition: 'border-color 0.2s ease',
            opacity: (!token || isSubmitting) ? 0.6 : 1,
            cursor: (!token || isSubmitting) ? 'not-allowed' : 'text'
          }}
          onFocus={(e) => e.target.style.borderColor = '#6B7280'}
          onBlur={(e) => e.target.style.borderColor = '#374151'}
        />
        <button
          type="submit"
          disabled={isSubmitting || !operatorCommand.trim() || !token}
          style={{
            padding: '0.rem 1rem',
            backgroundColor: (!token || isSubmitting || !operatorCommand.trim()) ? '#374151' : '#3B82F6',
            border: '1px solid #374151',
            borderRadius: '0.375rem',
            color: '#F3F4F6',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: (!token || isSubmitting || !operatorCommand.trim()) ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s ease',
            whiteSpace: 'nowrap'
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting && operatorCommand.trim() && token) {
              e.target.style.backgroundColor = '#2563EB';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting && operatorCommand.trim() && token) {
              e.target.style.backgroundColor = '#3B82F6';
            }
          }}
        >
          {isSubmitting ? 'Sending...' : 'Send'}
        </button>
      </form>
      {!token && (
        <p style={{
          marginTop: '0.25rem',
          fontSize: '0.75rem',
          color: '#9CA3AF',
          textAlign: 'center'
        }}>
          Log in to send commands
        </p>
      )}
    </div>
  );
};

export default OperatorChatbox;

